import { DefaultLayout } from '@c/layouts';
import ProductsPage from '@c/ProductsPage';
import SEO from '@c/SEO';
import { CategoryDocument } from '@util/firestore/categories';
import { BrandDocument } from '@util/types/firestore/brand';
import { ConditionDocument } from '@util/types/firestore/conditions';
import { getProducts } from '@util/firestore/products';
import { getProductQueryArgsFromContext } from '@util/getPropsFromConext';
import { PaginationFilters, ListingsFilters } from '@util/hooks/useProductsPage';
import { ProductDocument } from '@util/types/firestore/products';
import { Pagination } from '@util/types/paginator';
import { GetServerSideProps } from 'next';
import { BrandObj, getBrandBySlug } from 'pages/api/brands';
import { ParsedUrlQuery } from 'querystring';
import { ReactElement } from 'react';
import { getRedirectBySlug } from '@util/firestore/redirects'

interface BrandDetailPageProps {
  brand: BrandObj;
  initialProducts: ProductDocument[];
  initialPageInfo: Pagination;
  initialTopFilters: PaginationFilters;
  initialSideFilters: ListingsFilters;
  categoryOptions: CategoryDocument[];
  brandOptions: BrandDocument[];
  conditionOptions: ConditionDocument[];
}

interface Params extends ParsedUrlQuery {
  brand: string[];
}

const BrandDetailPage = ({
  brand,
  categoryOptions,
  conditionOptions,
  brandOptions,
  initialProducts,
  initialPageInfo,
  initialTopFilters,
  initialSideFilters,
}: BrandDetailPageProps) => {
  const pageTitle = `${brand.name} Cameras & Equipment | GearFocus`;
  const pageDescription = brand.description || `Shop for ${brand.name} cameras, lenses, and photography equipment. Find the best deals on new and used ${brand.name} gear.`;
  
  return (
    <>
      <SEO 
        title={pageTitle}
        description={pageDescription}
        ogImage={brand.image || '/images/brands-banner.jpg'}
        ogType="website"
        keywords={[brand.name, 'camera', 'photography', 'equipment', 'lens', 'gear', brand.name + ' camera', brand.name + ' lenses']}
      />
      
      <ProductsPage
        initialProducts={initialProducts}
        initialPageInfo={initialPageInfo}
        initialTopFilters={initialTopFilters}
        initialSideFilters={initialSideFilters}
        headerBreadcrumb={[
          { label: 'Home', href: '/' },
          { label: 'Brands', href: '/brands' },
          { label: brand.name, href: brand.url }
        ]}
        pageHeaderTitle={brand.name}
        pageHeaderBody={brand.description}
        brand={brand}
        hideBrands={true}
        pageType="brand"
        keyword={null}
        seller_id={null}
        conditionOptions={conditionOptions}
        categoryOptions={categoryOptions}
        brandOptions={brandOptions}
        slugToTitle={undefined}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps<
  BrandDetailPageProps
> = async (context) => {
  context.res.setHeader(
    'Cache-Control',
    'public, max-age=0, s-maxage=60, stale-while-revalidate=300'
  );
  const { brand } = context.params as Params;
  const brandObj = await getBrandBySlug(brand.toString());

  if (!brandObj) {
    const { results: redirects } = await getRedirectBySlug({ slug: `/brands/${brand.toString()}` });

    if (redirects) {
      return {
        redirect: {
          destination: redirects.destination,
          permanent: redirects.permanent
        }
      }
    } else {
      return {
        redirect: {
          destination: '/brands',
          permanent: true
        }
      }
    }
  }

  const contextQuery = context.query
  contextQuery.brand = [brandObj.name]
  const updatedContext = {...context, query: contextQuery}
    
  const args = await getProductQueryArgsFromContext(updatedContext);
  const response = await getProducts(args);

  // Trim brandOptions to filter-only fields (2 of 7) — saves ~200 KB per page
  const BRAND_KEYS = new Set(['id', 'name']);
  const trimBrand = (b: Record<string, unknown>) => {
    const t: Record<string, unknown> = {};
    for (const k of BRAND_KEYS) if (k in b) t[k] = b[k];
    return t;
  };

  // Trim categoryOptions to filter-only fields (5 of 14) — saves ~110 KB per page
  const CAT_KEYS = new Set(['id', 'name', 'level', 'parents']);
  const trimCat = (c: Record<string, unknown>) => {
    const t: Record<string, unknown> = {};
    for (const k of CAT_KEYS) if (k in c) t[k] = c[k];
    return t;
  };

  // Trim listings to display-only fields — saves ~40-50 KB per page
  const LISTING_KEYS = new Set([
    'id', 'title', 'slug', 'thumbnail', 'images', 'price', 'og_price',
    'on_sale', 'out_of_stock', 'stock', 'condition', 'brand', 'categories',
    'sku', 'bumps', 'favorite_count', 'is_auction', 'start_time', 'end_time',
    'is_verified', 'is_flat_rate', 'seller_id', 'shipping_costs', 'gearMaster',
    'external_source', 'integrations', 'description', 'account_id',
    'date_added', 'rank_score',
  ]);
  const trimListing = (l: Record<string, unknown>) => {
    const t: Record<string, unknown> = {};
    for (const k of LISTING_KEYS) if (k in l) t[k] = l[k];
    return t;
  };

  return {
    props: {
      categoryOptions: args.categoryOptions.map(c => trimCat(c as Record<string, unknown>)) as typeof args.categoryOptions,
      conditionOptions: args.conditionOptions,
      brandOptions: args.brandOptions.map(b => trimBrand(b as Record<string, unknown>)) as typeof args.brandOptions,
      brand: brandObj,
      initialProducts: response.results.map(r => trimListing(r as Record<string, unknown>)) as typeof response.results,
      initialPageInfo: response.pageInfo,
      initialTopFilters: {
        sort: args.sort,
        limit: args.limit,
        view: (context.query.view as 'list') || 'grid',
      },
      initialSideFilters: {
        minPrice: args.minPrice,
        maxPrice: args.maxPrice,
        condition: args.conditionFilters,
        category: args.categoryFilters,
        brand: [brandObj.name],
        deals: args.deals,
        useCases: args.useCases,
        additional: args.additionalFilters
      }
    },
  };
};

BrandDetailPage.getLayout = (page: ReactElement) => {
  return <DefaultLayout>{page}</DefaultLayout>;
};

export default BrandDetailPage;
